import { pageTitle } from '@palaccount/config';
import { DEFAULT_PAGE_SIZE, type ItemResponse, type ItemSortKey, type SortDir } from '@palaccount/contracts';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ImageIcon, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { attachmentUrl } from '@/features/attachments/api';
import { localizedName } from '@/lib/localized-name';
import { usePagedState, usePageRange } from '@/lib/paging';
import { readShowItemImages, writeShowItemImages } from '@/lib/personalization';
import { readableTextOn } from '@/lib/readable-color';
import { useBaseCurrency } from '@/lib/session';
import { cn } from '@/lib/utils';

import { useItemCategories, useItemStats, useManufacturers, usePagedItems } from './api';
import { trimZeros } from './conversion';
import { InventoryTabs } from './inventory-tabs';
import { StockStatusBadge } from './stock-chips';

const columnHelper = createColumnHelper<ItemResponse>();

/**
 * bugs: Items — "sort by name / category / type / price / qty / last
 * modified". Which table column answers to which server sort key; a column
 * absent here (Status) is not sortable. The server whitelists the key.
 */
const SORT_KEY_BY_COLUMN: Readonly<Record<string, ItemSortKey>> = {
  name: 'name',
  categoryName: 'category',
  type: 'type',
  unitPrice: 'price',
  onHand: 'onHand',
  updatedAt: 'updatedAt',
};
/**
 * IT-L5 — the row's picture: the newest image attachment (INV-H5), drawn from
 * the download route so the session cookie rides along, or a neutral
 * placeholder so the column keeps its width when an item has none. The image
 * is decoration beside a name that is already read out, so its alt is empty
 * and the placeholder is aria-hidden; the header carries the column's name.
 */
function ItemThumbnail({ attachmentId, name }: { attachmentId: string | null; name: string }) {
  const { t } = useTranslation();
  if (attachmentId === null) {
    return (
      <span
        aria-hidden
        title={t('items.noImage')}
        className="flex size-10 items-center justify-center rounded-md bg-muted text-muted-foreground"
      >
        <ImageIcon className="size-4" />
      </span>
    );
  }
  return (
    <img
      src={attachmentUrl(attachmentId)}
      alt=""
      title={name}
      loading="lazy"
      className="size-10 rounded-md border border-border object-cover"
    />
  );
}

/** Figures sit at the end of their cell, so their header does too. */
const END_ALIGNED = new Set(['unitPrice', 'onHand']);

interface SortState {
  key: ItemSortKey;
  dir: SortDir;
}

/**
 * bugs: Items — "default view = the last items edited when not searching".
 * With the search box AND every filter empty the list is newest-change-first;
 * the moment anything narrows it, the historical code order returns (a
 * search result in code order is what people are used to reading). An
 * explicit header click wins over both.
 */
function defaultSort(filtersEmpty: boolean): SortState | null {
  return filtersEmpty ? { key: 'updatedAt', dir: 'desc' } : null;
}

/**
 * IT-L8, literally: "the last 10 items edited or created" is the RECENT VIEW —
 * nothing typed, no filter, no header clicked — and it is ten rows a page,
 * newest change first. The moment a search, a filter or a chosen sort turns
 * the screen into the catalogue proper, the ordinary block (DEFAULT_PAGE_SIZE)
 * applies. The two sizes are two different questions — "what changed lately"
 * versus "show me the catalogue" — and a reader answers neither by scrolling
 * fifty rows of the other. The pager stays honest in both: the server reports
 * the total and the size it served, and the range is drawn from those.
 */
const RECENT_ITEMS_PAGE_SIZE = 10;

function nextSort(current: SortState | null, key: ItemSortKey): SortState {
  if (current?.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  // A date reads newest-first on the first click; everything else A→Z / low→high.
  return { key, dir: key === 'updatedAt' ? 'desc' : 'asc' };
}

function TypeChip({ type }: { type: ItemResponse['type'] }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-medium',
        // Foreground ink, not text-primary: on the /10 tint of itself the
        // primary ink measured 3.57:1 in dark (linear) and under the three
        // D20-darkened accents 4.13-4.19 in light. The tinted fill still
        // carries the brand colour.
        type === 'product' && 'bg-primary/10 text-foreground',
        type === 'service' && 'bg-muted text-muted-foreground',
        type === 'bundle' && 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
      )}
    >
      {t(`items.type.${type}`)}
    </span>
  );
}

function StatusChip({ active, deleted }: { active: boolean; deleted: boolean }) {
  const { t } = useTranslation();
  // IT-N4: a deleted item says so before anything else — it is only ever on
  // this list through the Deleted filter.
  if (deleted) {
    return (
      <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
        {t('items.deleted')}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-medium',
        active
          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
          : 'bg-muted text-muted-foreground',
      )}
    >
      {t(active ? 'common.active' : 'common.inactive')}
    </span>
  );
}

/**
 * IT-L4 — one chip per warehouse, "<name> <qty>", tinted with the warehouse's
 * own colour. The ink is COMPUTED from the fill (NFR-11, `readable-color.ts`):
 * a colour comes from data, so fixed white text is exactly the defect the
 * assets page had. No colour = the muted surface. One warehouse = the plain
 * total, which the caller draws instead.
 */
function WarehouseQtyChips({ item }: { item: ItemResponse }) {
  const { t } = useTranslation();
  return (
    <span className="flex flex-wrap items-center justify-end gap-1">
      {item.stock.map((row) => (
        <span
          key={row.warehouseId}
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-medium',
            row.warehouseColour === null && 'bg-muted text-muted-foreground',
          )}
          style={
            row.warehouseColour === null
              ? undefined
              : { backgroundColor: row.warehouseColour, color: readableTextOn(row.warehouseColour) }
          }
          title={t('items.warehouseQty', { warehouse: row.warehouseName, qty: trimZeros(row.onHand) })}
        >
          {row.warehouseName} <Ltr value={trimZeros(row.onHand)} className="font-mono" />
        </span>
      ))}
    </span>
  );
}

/**
 * MD-03 §4a — the catalog: KPI strip (counting the WHOLE catalog, never the
 * filtered view), a search box matching code/name/category, and the
 * dropdowns narrowing together.
 *
 * PAGED (NFR-42). Two things had to change together, and the second is the
 * one that is easy to miss: the list now asks for one page, AND the KPI strip
 * asks the server for four numbers instead of fetching the whole catalogue a
 * second time to count it in the browser. Paging only the table would have
 * taken a 51 MB screen to 25 MB.
 *
 * The page lives in the URL, so a link to page 7 of the catalogue is a link
 * somebody can send — see `lib/paging.ts`.
 *
 * bugs: Items — the ROW is not a button any more ("clicking the rectangle
 * does nothing; clicking parts does specific things"): the name opens the
 * item, the category narrows the list to it, the on-hand figure opens the
 * item in Stock Levels. Column headers sort (aria-sort says which).
 */
export function ItemsListPage() {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState('');
  const [type, setType] = useState<'' | 'product' | 'service' | 'bundle'>('');
  const [categoryId, setCategoryId] = useState('');
  const [manufacturerId, setManufacturerId] = useState('');
  const [status, setStatus] = useState<'' | 'active' | 'inactive' | 'deleted'>('');
  const [alerts, setAlerts] = useState<'' | 'tracked' | 'untracked'>('');
  const [chosenSort, setChosenSort] = useState<SortState | null>(null);
  // IT-L5: the picture column, remembered on this device (lib/personalization).
  const [showImages, setShowImages] = useState(readShowItemImages);

  useEffect(() => {
    document.title = pageTitle(t('items.title'));
  }, [t]);

  const filtersEmpty =
    q.trim() === '' &&
    type === '' &&
    categoryId === '' &&
    manufacturerId === '' &&
    status === '' &&
    alerts === '';
  const sort = chosenSort ?? defaultSort(filtersEmpty);
  // IT-L8 — see RECENT_ITEMS_PAGE_SIZE. Every setter below goes through
  // `paging.change`, so leaving the recent view also returns to page 1: page
  // 3 of ten is not page 3 of fifty.
  const recentView = filtersEmpty && chosenSort === null;
  const paging = usePagedState(recentView ? RECENT_ITEMS_PAGE_SIZE : DEFAULT_PAGE_SIZE);
  const filters = {
    q: q.trim() || undefined,
    type: type || undefined,
    categoryId: categoryId || undefined,
    manufacturerId: manufacturerId || undefined,
    status: status || undefined,
    alerts: alerts || undefined,
    sort: sort?.key,
    dir: sort?.dir,
  };
  const { data, isPending, isError } = usePagedItems(filters, paging.page, paging.pageSize);
  const items = data?.items;
  // The KPI strip counts the whole catalog (§4a) — four count(*)s, in SQL.
  const { data: stats } = useItemStats();
  const range = usePageRange(data, paging.pageSize);
  const { data: categories } = useItemCategories(true);
  const { data: manufacturers } = useManufacturers(true);

  // `change` is the memoised setter-wrapper from the paging hook (a filter
  // change resets the page), stable across renders — so the columns are too.
  const { change } = paging;
  const columns = useMemo(
    () => [
      // IT-L5: the picture first — hidden as a whole column, not blanked per
      // cell, when the person switches images off (columnVisibility below).
      columnHelper.display({
        id: 'thumbnail',
        header: () => <span className="sr-only">{t('items.colImage')}</span>,
        cell: (info) => (
          <ItemThumbnail
            attachmentId={info.row.original.thumbnailAttachmentId}
            name={localizedName(info.row.original, i18n.language)}
          />
        ),
      }),
      columnHelper.accessor('name', {
        header: () => t('items.colItem'),
        cell: (info) => (
          <div className="grid">
            <Link
              to={`/inventory/items/${info.row.original.id}`}
              className="font-medium hover:underline"
            >
              {/* S5: the Arabic twin on an Arabic screen, the name otherwise. */}
              {localizedName(info.row.original, i18n.language)}
            </Link>
            <span className="text-xs text-muted-foreground">
              <Ltr value={info.row.original.code} className="font-mono" />
              {info.row.original.variantCount > 0 ? (
                <> · {t('items.variants', { count: info.row.original.variantCount })}</>
              ) : null}
            </span>
          </div>
        ),
      }),
      columnHelper.accessor('categoryName', {
        header: () => t('items.colCategory'),
        // Clicking a category narrows the list to it (and, server-side, to
        // its descendants) — the quickest "show me the rest of these".
        cell: (info) => (
          <button
            type="button"
            title={t('items.filterByCategory')}
            onClick={() => change(setCategoryId)(info.row.original.categoryId)}
            className="text-start text-sm hover:underline"
          >
            {info.getValue()}
          </button>
        ),
      }),
      columnHelper.accessor('type', {
        header: () => t('items.colType'),
        cell: (info) => <TypeChip type={info.getValue()} />,
      }),
      columnHelper.accessor('unitPrice', {
        header: () => t('items.colPrice'),
        cell: (info) => (
          <div className="text-end">
            <Amount value={info.getValue()} currency={baseCurrency} />
          </div>
        ),
      }),
      columnHelper.display({
        id: 'onHand',
        header: () => t('items.colOnHand'),
        // BR-INV-05-03: the badge speaks only when something is wrong — an OK
        // item shows its figure and nothing else (and an item with Track stock
        // alerts OFF is always "ok" on the wire). The figure is a link into
        // Stock Levels, searched to this item's code.
        cell: (info) => (
          <div className="flex items-center justify-end gap-2">
            <StockStatusBadge status={info.row.original.stockStatus} />
            <Link
              to={`/inventory/stock?q=${encodeURIComponent(info.row.original.code)}`}
              title={t('items.openStock')}
              className="hover:underline"
            >
              {/* IT-L4: several warehouses → one tinted chip each; one (or
                  none) → the plain total, which says the same thing shorter. */}
              {info.row.original.stock.length > 1 ? (
                <WarehouseQtyChips item={info.row.original} />
              ) : (
                <Ltr value={trimZeros(info.row.original.totalOnHand)} className="font-mono" />
              )}
            </Link>
          </div>
        ),
      }),
      // bugs: Items — "add the last-modified date". The in-house stamp form
      // (the authorship line's), left-to-right in either language.
      columnHelper.accessor('updatedAt', {
        header: () => t('items.colUpdated'),
        cell: (info) => (
          <Ltr
            value={(info.getValue() ?? '').slice(0, 16).replace('T', ' ')}
            className="font-mono text-xs text-muted-foreground"
          />
        ),
      }),
      columnHelper.accessor('isActive', {
        header: () => t('items.colStatus'),
        cell: (info) => (
          <StatusChip active={info.getValue()} deleted={info.row.original.deletedAt !== null} />
        ),
      }),
    ],
    // `baseCurrency` is a DEPENDENCY, not an incidental capture: it arrives
    // with the session a moment after the first render, and a memo that did
    // not list it would keep the pre-session fallback for ever — the stale-
    // closure version of the literal this replaced.
    [t, i18n.language, baseCurrency, change],
  );

  const table = useReactTable({
    data: items ?? [],
    columns,
    state: { columnVisibility: { thumbnail: showImages } },
    getCoreRowModel: getCoreRowModel(),
  });

  const total = stats?.total ?? 0;
  const active = stats?.active ?? 0;
  const lowStock = stats?.lowStock ?? 0;
  const outOfStock = stats?.outOfStock ?? 0;

  return (
    <div className="grid gap-4">
      <InventoryTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('items.title')}</h2>
        <Gated permission="inventory.create" reasonKey="perm.cannot.createItems">
          <Button asChild>
            <Link to="/inventory/items/new">
              <Plus aria-hidden /> {t('common.new')}
            </Link>
          </Button>
        </Gated>
      </div>

      {/* The KPI strip — whole-catalog counts (§4a), stock included. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card>
          <CardContent className="grid gap-1 py-4">
            <span className="text-xs text-muted-foreground">{t('items.kpiTotal')}</span>
            <span className="font-mono text-2xl font-bold">{total}</span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid gap-1 py-4">
            <span className="text-xs text-muted-foreground">{t('items.kpiActive')}</span>
            <span className="font-mono text-2xl font-bold text-emerald-700 dark:text-emerald-400">
              {active}
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid gap-1 py-4">
            <span className="text-xs text-muted-foreground">{t('items.kpiLowStock')}</span>
            <span className="font-mono text-2xl font-bold text-amber-700 dark:text-amber-400">
              {lowStock}
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid gap-1 py-4">
            <span className="text-xs text-muted-foreground">{t('items.kpiOutOfStock')}</span>
            <span className="font-mono text-2xl font-bold text-red-600 dark:text-red-400">
              {outOfStock}
            </span>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="grid gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-[320px]">
              <Search
                className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                aria-label={t('items.searchLabel')}
                placeholder={t('items.searchPh')}
                value={q}
                onChange={(event) => paging.change(setQ)(event.target.value)}
                className="h-10 ps-9 text-sm"
              />
            </div>
            <NativeSelect
              aria-label={t('items.filterCategory')}
              value={categoryId}
              onChange={(event) => change(setCategoryId)(event.target.value)}
              className="h-10 w-auto text-sm"
            >
              <option value="">{t('items.filterCategoryAll')}</option>
              {(categories ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </NativeSelect>
            {/* IT-S6: one maker's items. */}
            <NativeSelect
              aria-label={t('items.filterManufacturer')}
              value={manufacturerId}
              onChange={(event) => change(setManufacturerId)(event.target.value)}
              className="h-10 w-auto text-sm"
            >
              <option value="">{t('items.filterManufacturerAll')}</option>
              {(manufacturers ?? []).map((maker) => (
                <option key={maker.id} value={maker.id}>
                  {localizedName(maker, i18n.language)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('items.filterType')}
              value={type}
              onChange={(event) => paging.change(setType)(event.target.value as typeof type)}
              className="h-10 w-auto text-sm"
            >
              <option value="">{t('items.filterTypeAll')}</option>
              <option value="product">{t('items.type.product')}</option>
              <option value="service">{t('items.type.service')}</option>
              <option value="bundle">{t('items.type.bundle')}</option>
            </NativeSelect>
            <NativeSelect
              aria-label={t('items.filterStatus')}
              value={status}
              onChange={(event) => paging.change(setStatus)(event.target.value as typeof status)}
              className="h-10 w-auto text-sm"
            >
              <option value="">{t('items.filterStatusAll')}</option>
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
              {/* IT-N4: the one place a soft-deleted item is listed. */}
              <option value="deleted">{t('items.filterStatusDeleted')}</option>
            </NativeSelect>
            {/* bugs: Items — Tracked / Untracked / All on "Track stock alerts". */}
            <NativeSelect
              aria-label={t('items.filterAlerts')}
              value={alerts}
              onChange={(event) => paging.change(setAlerts)(event.target.value as typeof alerts)}
              className="h-10 w-auto text-sm"
            >
              <option value="">{t('items.filterAlertsAll')}</option>
              <option value="tracked">{t('items.filterAlertsTracked')}</option>
              <option value="untracked">{t('items.filterAlertsUntracked')}</option>
            </NativeSelect>
            {/* IT-L5: "Show images" — a view choice, so it never touches the
                query or the page; it is written to this device the moment it
                is tapped. */}
            <Label htmlFor="items-show-images" className="flex items-center gap-2 font-normal">
              <Checkbox
                id="items-show-images"
                checked={showImages}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  setShowImages(next);
                  writeShowItemImages(next);
                }}
              />
              {t('items.showImages')}
            </Label>
          </div>

          {/* IT-L8: say WHY there are ten rows, so the recent view is not
              mistaken for a ten-item catalogue. */}
          {recentView && !isPending && !isError && (data?.total ?? 0) > 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="items-recent-hint">
              {t('items.recentHint', { count: RECENT_ITEMS_PAGE_SIZE })}
            </p>
          ) : null}

          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : table.getRowModel().rows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-sm text-muted-foreground">
                {total === 0 ? t('items.empty') : t('items.noMatch')}
              </p>
              {/* The empty-state offer exists only when the catalogue is
                  genuinely empty — "no matches" is a filter, not a beginning —
                  and greys on the permission. */}
              {total === 0 ? (
                <Gated permission="inventory.create" reasonKey="perm.cannot.createItems">
                  <Button asChild variant="outline">
                    <Link to="/inventory/items/new">
                      <Plus aria-hidden /> {t('common.new')}
                    </Link>
                  </Button>
                </Gated>
              ) : null}
            </div>
          ) : (
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const key = SORT_KEY_BY_COLUMN[header.column.id];
                      const sorted = key !== undefined && sort?.key === key ? sort.dir : null;
                      const label = flexRender(header.column.columnDef.header, header.getContext());
                      const end = END_ALIGNED.has(header.column.id);
                      return (
                        <TableHead
                          key={header.id}
                          aria-sort={
                            key === undefined
                              ? undefined
                              : sorted === 'asc'
                                ? 'ascending'
                                : sorted === 'desc'
                                  ? 'descending'
                                  : 'none'
                          }
                          className={cn(end && 'text-end')}
                        >
                          {key === undefined ? (
                            label
                          ) : (
                            <button
                              type="button"
                              onClick={() => paging.change(setChosenSort)(nextSort(sort, key))}
                              className={cn(
                                'inline-flex items-center gap-1 hover:text-foreground',
                                end && 'flex-row-reverse',
                              )}
                            >
                              {label}
                              {sorted === 'asc' ? (
                                <ArrowUp className="size-3.5" aria-hidden />
                              ) : sorted === 'desc' ? (
                                <ArrowDown className="size-3.5" aria-hidden />
                              ) : (
                                <ArrowUpDown className="size-3.5 opacity-50" aria-hidden />
                              )}
                            </button>
                          )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* NFR-42 — the range, the total and the two steps (DSH-09 §5f). */}
          {!isPending && !isError && (data?.total ?? 0) > 0 ? (
            <Pager
              page={paging.page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={paging.setPage}
              label={t('items.title')}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
